/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { Component } from 'react';
import PropTypes from "prop-types";
import { copyToClipboard } from '../../services';
import { EuiToolTip } from '../tool_tip';
export class EuiCopy extends Component {
  static defaultProps = {
    afterMessage: 'Copied'
  };

  constructor(props) {
    super(props);
    this.state = {
      tooltipText: this.props.beforeMessage
    };
  }

  copy = () => {
    const isCopied = copyToClipboard(this.props.textToCopy);

    if (isCopied) {
      this.setState({
        tooltipText: this.props.afterMessage
      });
    }
  };
  resetTooltipText = () => {
    this.setState({
      tooltipText: this.props.beforeMessage
    });
  };

  render() {
    const {
      children,
      textToCopy,
      beforeMessage,
      afterMessage,
      ...rest
    } = this.props;
    return (// See `src/components/tool_tip/tool_tip.js` for explanation of below eslint-disable
      // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
      <EuiToolTip content={this.state.tooltipText} onMouseOut={this.resetTooltipText} {...rest}>
        {children(this.copy)}
      </EuiToolTip>
    );
  }

}
EuiCopy.propTypes = {
  /**
     * Text that will be copied to clipboard when copy function is executed.
     */
  textToCopy: PropTypes.string.isRequired,

  /**
     * Tooltip message displayed before copy function is called.
     */
  beforeMessage: PropTypes.node,

  /**
     * Tooltip message displayed after copy function is called that lets the user know that
     * 'textToCopy' has been copied to the clipboard.
     */
  afterMessage: PropTypes.node,

  /**
     * Function that must return a component. First argument is 'copy' function.
     * Use your own logic to create the component that users interact with when triggering copy.
     */
  children: PropTypes.func.isRequired,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};

try {
  EuiCopy.__docgenInfo = {
    description: '',
    displayName: 'EuiCopy',
    methods: [],
    props: {
      textToCopy: {
        defaultValue: null,
        description: 'Text that will be copied to clipboard when copy function is executed.',
        name: 'textToCopy',
        parent: {
          fileName: 'app/src/components/copy/copy.tsx',
          name: 'EuiCopyProps'
        },
        required: true,
        type: {
          name: 'string'
        }
      },
      beforeMessage: {
        defaultValue: null,
        description: 'Tooltip message displayed before copy function is called.',
        name: 'beforeMessage',
        parent: {
          fileName: 'app/src/components/copy/copy.tsx',
          name: 'EuiCopyProps'
        },
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      afterMessage: {
        defaultValue: {
          value: 'Copied'
        },
        description: 'Tooltip message displayed after copy function is called that lets the user know that\n\'textToCopy\' has been copied to the clipboard.',
        name: 'afterMessage',
        parent: {
          fileName: 'app/src/components/copy/copy.tsx',
          name: 'EuiCopyProps'
        },
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      children: {
        defaultValue: null,
        description: 'Function that must return a component. First argument is \'copy\' function.\nUse your own logic to create the component that users interact with when triggering copy.',
        name: 'children',
        parent: {
          fileName: 'app/src/components/copy/copy.tsx',
          name: 'EuiCopyProps'
        },
        required: true,
        type: {
          name: '(copy: () => void) => ReactElement'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      id: {
        defaultValue: null,
        description: 'Unless you provide one, this will be randomly generated.',
        name: 'id',
        parent: {
          fileName: 'app/src/components/tool_tip/tool_tip.tsx',
          name: 'EuiToolTipProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      title: {
        defaultValue: null,
        description: 'An optional title for your tooltip.',
        name: 'title',
        parent: {
          fileName: 'app/src/components/tool_tip/tool_tip.tsx',
          name: 'EuiToolTipProps'
        },
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      onMouseOut: {
        defaultValue: null,
        description: 'If supplied, called when mouse movement causes the tool tip to be\nhidden.',
        name: 'onMouseOut',
        parent: {
          fileName: 'app/src/components/tool_tip/tool_tip.tsx',
          name: 'EuiToolTipProps'
        },
        required: false,
        type: {
          name: '(event: MouseEvent<HTMLSpanElement, MouseEvent>) => void'
        }
      },
      display: {
        defaultValue: null,
        description: 'Common display alternatives for the anchor wrapper',
        name: 'display',
        parent: {
          fileName: 'app/src/components/tool_tip/tool_tip.tsx',
          name: 'EuiToolTipProps'
        },
        required: false,
        type: {
          name: 'enum',
          raw: '"inlineBlock" | "block"',
          value: [{
            value: '"inlineBlock"'
          }, {
            value: '"block"'
          }]
        }
      },
      anchorClassName: {
        defaultValue: null,
        description: 'Passes onto the the trigger.',
        name: 'anchorClassName',
        parent: {
          fileName: 'app/src/components/tool_tip/tool_tip.tsx',
          name: 'EuiToolTipProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      position: {
        defaultValue: null,
        description: 'Suggested position. If there is not enough room for it this will be changed.',
        name: 'position',
        parent: {
          fileName: 'app/src/components/tool_tip/tool_tip.tsx',
          name: 'EuiToolTipProps'
        },
        required: false,
        type: {
          name: 'enum',
          raw: 'EuiPopoverPosition',
          value: [{
            value: '"left"'
          }, {
            value: '"right"'
          }, {
            value: '"top"'
          }, {
            value: '"bottom"'
          }]
        }
      },
      content: {
        defaultValue: null,
        description: 'The main content of your tooltip.',
        name: 'content',
        parent: {
          fileName: 'app/src/components/tool_tip/tool_tip.tsx',
          name: 'EuiToolTipProps'
        },
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      delay: {
        defaultValue: null,
        description: 'Delay before showing tooltip. Good for repeatable items.',
        name: 'delay',
        parent: {
          fileName: 'app/src/components/tool_tip/tool_tip.tsx',
          name: 'EuiToolTipProps'
        },
        required: false,
        type: {
          name: 'enum',
          raw: 'ToolTipDelay',
          value: [{
            value: '"regular"'
          }, {
            value: '"long"'
          }]
        }
      }
    },
    extendedInterfaces: ['EuiCopyProps', 'CommonProps', 'EuiToolTipProps']
  };
} catch (e) {}